import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  setInvalidPendingBuildings,
  showToast,
  undoPendingManeuverAction,
  undoPendingMovementAction,
  undoPendingRecruitmentAction,
  undoPendingSupplyAction,
} from '../../../redux/actions';
import {
  selectInvalidPendingBuildings,
  selectPendingBuildings,
  selectPendingManeuverActions,
  selectPendingMovementActions,
  selectPendingRecruitmentActions,
  selectPendingSupply,
  selectPhase,
  selectSharedGameState,
  selectUnits,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {findUndoableUnitMove} from "../../../utils/gameState/movement/findUndoableUnitMove";
import {PHASES, WAR_PHASE_STAGES} from "../../../constants/phases";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {getUndoablePendingSupplyActions} from "../../../utils/gameState/supply/getUndoablePendingSupplyActions";
import {selectIsTutorialActive} from "../../../redux/reducers/privateStateReducer/selectors";
import {
  checkIfTutorialUndoAllowed,
  checkIfTutorialUndoButtonRevealed
} from "../../../utils/tutorial/useTutorialUiFlags";
import {createUndoConstructionAction} from "../../../utils/gameState/construction/createUndoConstructionAction";
import {filterPlayerPendingSupply} from "../../../utils/gameState/supply/parallelSupply";
import {MASKED_ICON_TONES, MaskedIcon} from "../../coloredIcons/MaskedIcon/MaskedIcon";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from "../../../utils/gameState/supply/parallelSupplySelectors";
import {GAME_BUTTON_SIZES, GAME_BUTTON_VARIANTS, GameButton} from '../../gameUi/GameButton';

// Есть ли что отменять в фазе снабжения. В одновременной фазе сетевой игры
// снабжение раздают все разом, и отменять можно только СВОЮ раздачу — ту, что
// принадлежит игроку, за которого клиент раздает снабжение (без него — нечего).
// В поочередной — вся отложенная раздача, как всегда (она и так текущего игрока)
const selectHasUndoablePendingSupply = (state) => {
  if (selectPhase(state) !== PHASES.SUPPLY) {
    return false;
  }
  const pendingSupply = selectPendingSupply(state) || [];
  if (!selectIsParallelSupplyPhase(state)) {
    return getUndoablePendingSupplyActions(pendingSupply).length > 0;
  }
  const supplyPlayer = selectSupplyActingPlayer(state);
  if (!supplyPlayer) {
    return false;
  }
  const playerPendingSupply = filterPlayerPendingSupply(pendingSupply, selectSharedGameState(state), supplyPlayer.id);
  return getUndoablePendingSupplyActions(playerPendingSupply).length > 0;
};

const UndoButton = () => {
  const dispatch = useDispatch();
  const isTutorialActive = useSelector(selectIsTutorialActive);
  const isTutorialUndoRevealed = useSelector(checkIfTutorialUndoButtonRevealed);
  const isTutorialUndoAllowed = useSelector(checkIfTutorialUndoAllowed);
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const pendingBuildings = useSelector(selectPendingBuildings);
  const pendingRecruitmentActions = useSelector(selectPendingRecruitmentActions);
  const pendingMovementActions = useSelector(selectPendingMovementActions);
  const hasUndoablePendingSupply = useSelector(selectHasUndoablePendingSupply);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  const supplyPlayer = useSelector(selectSupplyActingPlayer);
  const pendingManeuverActions = useSelector(selectPendingManeuverActions);
  const invalidBuildings = useSelector(selectInvalidPendingBuildings);
  const units = useSelector(selectUnits);

  // В обучении кнопка скрыта до шага знакомства с ней (см.
  // checkIfTutorialUndoButtonRevealed)
  if (isTutorialActive && !isTutorialUndoRevealed) {
    return null;
  }

  if (phase === PHASES.WAR) {
    if (warPhaseStage === WAR_PHASE_STAGES.SHOOTING || warPhaseStage === WAR_PHASE_STAGES.COMBAT
      || warPhaseStage === WAR_PHASE_STAGES.ABILITIES) {
      return null;
    }
  }
  const handleUndo = () => {
    // В обучении отмена недоступна (кроме шага знакомства, который просит
    // откатить постройку — allow: 'undo'): вместо отката действия
    // показываем info-подсказку со звуком ошибки
    if (isTutorialActive && !isTutorialUndoAllowed) {
      dispatch(showToast('В обучении отмена действий недоступна', 'warning', 3000, true));
      REGISTRY_SINGLETON.getSoundManager()?.playToastError?.();
      return;
    }

    REGISTRY_SINGLETON.getSoundManager().playUndoClick();
    if (phase === PHASES.CONSTRUCTION) {
      // Находим последнее действие текущего игрока
      const lastBuilding = pendingBuildings[pendingBuildings.length - 1];

      if (lastBuilding) {
        dispatch(createUndoConstructionAction(lastBuilding));
        if (invalidBuildings && invalidBuildings.length > 0) {
          dispatch(setInvalidPendingBuildings([]));
        }
      }
    } else if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT) {
      const lastRecruitmentAction = pendingRecruitmentActions[pendingRecruitmentActions.length - 1];
      if (!lastRecruitmentAction) {
        return;
      }
      dispatch(undoPendingRecruitmentAction(lastRecruitmentAction));
    } else if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.MOVEMENT) {
      dispatch(undoPendingMovementAction());
    } else if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.MANEUVER) {
      dispatch(undoPendingManeuverAction());
    } else if (phase === PHASES.SUPPLY) {
      if (!isParallelSupplyPhase) {
        dispatch(undoPendingSupplyAction());
      } else if (supplyPlayer) {
        // Одновременная фаза: отменяется последнее действие СВОЕГО игрока
        dispatch(undoPendingSupplyAction(supplyPlayer.id));
      }
    }
  };

  let enabled = false;
  if (phase === PHASES.SUPPLY) {
    if (hasUndoablePendingSupply) {
      enabled = true;
    }
  } else if (phase === PHASES.CONSTRUCTION) {
    const notCommitedPendingBuildings = pendingBuildings.filter(building => !building.isCommited);
    if (notCommitedPendingBuildings.length > 0) {
      enabled = true;
    }
  } else if (phase === PHASES.WAR) {
    switch (warPhaseStage) {
      case WAR_PHASE_STAGES.RECRUITMENT:
        if (pendingRecruitmentActions.length > 0) {
          enabled = true;
        }
        break;
      case WAR_PHASE_STAGES.MOVEMENT:
        // Считаем ТОЙ ЖЕ функцией, что и сам reducer (UNDO_PENDING_MOVEMENT_ACTION),
        // а не приблизительной проверкой "есть хоть одна необратимая запись" -
        // кнопка снимает строго ПОСЛЕДНЕЕ перемещение, и именно оно может
        // быть заблокировано занятой исходной ячейкой (отряд, пришедший туда
        // позже из-за срабатывания ловушки Гоблинов, design.md Decision 8),
        // даже когда более ранние записи в списке ещё отменяемы - иначе
        // кнопка выглядела бы активной, а клик по ней ничего не делал (найдено
        // внешним ревью ветки add-goblins-faction)
        if (findUndoableUnitMove(pendingMovementActions, units) !== null) {
          enabled = true;
        }
        break;
      case WAR_PHASE_STAGES.MANEUVER:
        // Тот же принцип, что и у перемещения выше
        if (findUndoableUnitMove(pendingManeuverActions, units) !== null) {
          enabled = true;
        }
        break;
      default:
    }
  }

  return (
    <GameButton
      variant={GAME_BUTTON_VARIANTS.SUNK}
      size={GAME_BUTTON_SIZES.ICON}
      onClick={handleUndo}
      data-tutorial-select={'undo_button'}
      className={'footer-heading-btn'}
      disabled={!enabled}
    >
      <MaskedIcon
        src={'/img/icons/return.png'}
        alt={'Undo'}
        tone={MASKED_ICON_TONES.CONTROLS}
      />
    </GameButton>
  );
};

export default UndoButton;
